import React, { useEffect, useState } from 'react'
import { Card, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { getOwnerAudit, reconcileOwners, type OwnerAudit } from './crmApi'

/**
 * Owner identity (Phase 2.9).
 *
 * Three "owners" have coexisted here without ever being reconciled: the deal's owner_id (meant
 * to be an agent — but the CRM's UI has always offered a MOCK user list, 'u1' = Lee, 'u2' =
 * Sarah), the lead_owner text an import wrote ("Lee"), and the real agents who actually have
 * logins and socket rooms.
 *
 * A lead owned by 'u1' is owned by nobody. Alerts to it were delivered to an empty room and
 * reported as delivered — a failure indistinguishable from success. This finds them and lets
 * you repair the mapping, one owner at a time, on purpose.
 *
 * Nothing is reassigned automatically: putting a lead on the WRONG person is worse than
 * leaving it unassigned.
 */
export const CrmOwners: React.FC = () => {
  const [audit, setAudit] = useState<OwnerAudit | null>(null)
  const [choice, setChoice] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const load = () => { getOwnerAudit().then(setAudit).catch(() => setAudit(null)) }
  useEffect(load, [])

  useEffect(() => {
    if (!audit) return
    // Pre-select the suggestion where there is one — but it still needs a human to press Apply.
    const pre: Record<string, string> = {}
    audit.orphans.forEach((o) => { if (o.suggestion) pre[o.ownerId] = o.suggestion.agentId })
    setChoice(pre)
  }, [audit])

  const apply = async () => {
    const mapping = Object.fromEntries(Object.entries(choice).filter(([, v]) => v))
    if (!Object.keys(mapping).length) return crmToast('Nothing selected', 'error')
    setBusy(true)
    try {
      const dry = await reconcileOwners(mapping, false)
      const moved = dry.results.filter((r) => r.applied).reduce((n, r) => n + r.leads, 0)
      crmToast(`Reassigned ${moved} lead(s) to real agents`, 'success')
      load()
    } catch {
      crmToast('Could not reassign', 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!audit) return null

  const healthy = audit.unresolvable === 0

  return (
    <Card
      title="Lead ownership"
      subtitle="An alert can only reach a real person. A lead owned by a mock id is owned by nobody."
    >
      <div className="flex gap-2 flex-wrap mb-3">
        <div className="flex-1 min-w-[120px] px-3 py-2 rounded-lg bg-surface-secondary">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Reaches a real person</p>
          <p className="text-lg font-bold" style={{ color: '#166534' }}>{audit.resolvable}</p>
        </div>
        <div className="flex-1 min-w-[120px] px-3 py-2 rounded-lg" style={{ background: audit.unresolvable ? '#FEE2E2' : '#F8FAFC' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: audit.unresolvable ? '#991B1B' : '#94A3B8' }}>Owned by nobody</p>
          <p className="text-lg font-bold" style={{ color: audit.unresolvable ? '#991B1B' : '#94A3B8' }}>{audit.unresolvable}</p>
        </div>
        <div className="flex-1 min-w-[120px] px-3 py-2 rounded-lg bg-surface-secondary">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">No owner set</p>
          <p className="text-lg font-bold text-txt-primary">{audit.unowned}</p>
        </div>
      </div>

      <p className="text-[11px] leading-relaxed px-3 py-2 rounded-lg" style={healthy ? { background: '#F0FDF4', color: '#166534' } : { background: '#FEF3C7', color: '#92400E' }}>
        {audit.note}
      </p>

      {audit.orphans.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">
            Repair — map each unreachable owner to a real agent
          </p>
          <div className="rounded-xl border border-border overflow-hidden">
            {audit.orphans.map((o) => (
              <div key={o.ownerId} className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-txt-primary truncate">
                    <span className="font-mono">{o.ownerId}</span>
                    {o.leadOwnerText && <span className="text-txt-secondary"> · “{o.leadOwnerText}”</span>}
                  </p>
                  <p className="text-[11px] text-txt-tertiary">
                    {o.leads} lead{o.leads === 1 ? '' : 's'}
                    {o.suggestion && <span style={{ color: '#0369A1' }}> · suggested: {o.suggestion.name} ({o.suggestion.why})</span>}
                  </p>
                </div>
                <select
                  value={choice[o.ownerId] || ''}
                  onChange={(e) => setChoice((c) => ({ ...c, [o.ownerId]: e.target.value }))}
                  className="px-2 py-1.5 rounded-lg bg-surface-secondary border border-border text-xs outline-none focus:border-brand-500"
                >
                  <option value="">Leave unassigned</option>
                  {audit.agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" loading={busy} onClick={apply}>Reassign selected</Button>
            <span className="text-[11px] text-txt-tertiary">
              Nothing is reassigned until you press this. A lead on the wrong person is worse than one on nobody.
            </span>
          </div>
        </div>
      )}
    </Card>
  )
}
